import { Action } from '../../actions';
import {
    AdditionalTargetingChangesCancelAction,
    AdditionalTargetingChangesSaveAction,
    AdditionalTargetingInternalCloseAction,
    AdditionalTargetingInternalOpenAction,
    AdditionalTargetingUidsControlChangeAction,
    AdditionalTargetingUidsValueChangeAction,
    BrowserEnginesControlChangeAction,
    BrowserEnginesValueChangeAction,
    BrowserNamesControlChangeAction,
    BrowserNamesValueChangeAction,
    CANCEL_ADDITIONAL_TARGETING_CHANGES,
    CHANGE_ADDITIONAL_TARGETING_UIDS_CONTROL,
    CHANGE_ADDITIONAL_TARGETING_UIDS_VALUE,
    CHANGE_BROWSER_ENGINES_CONTROL,
    CHANGE_BROWSER_ENGINES_VALUE,
    CHANGE_BROWSER_NAMES_CONTROL,
    CHANGE_BROWSER_NAMES_VALUE,
    CHANGE_DEVICE_NAMES_CONTROL,
    CHANGE_DEVICE_NAMES_VALUE,
    CHANGE_DEVICE_VENDORS_CONTROL,
    CHANGE_DEVICE_VENDORS_VALUE,
    CHANGE_INTERFACE_LANGS_CONTROL,
    CHANGE_INTERFACE_LANGS_VALUE,
    CHANGE_INTERNAL_NETWORK_CONTROL,
    CHANGE_IS_BROWSER_CONTROL,
    CHANGE_IS_MOBILE_CONTROL,
    CHANGE_IS_TABLET_CONTROL,
    CHANGE_IS_TOUCH_CONTROL,
    CHANGE_OS_FAMILIES_CONTROL,
    CHANGE_OS_FAMILIES_VALUE,
    CHANGE_OS_NAMES_CONTROL,
    CHANGE_OS_NAMES_VALUE,
    CHANGE_PRIORITY_VALUE,
    CHANGE_QUERY_REFERERS_CONTROL,
    CHANGE_QUERY_REFERERS_VALUE,
    CHANGE_USER_AGENTS_CONTROL,
    CHANGE_USER_AGENTS_VALUE,
    CLOSE_ADDITIONAL_TARGETING_INTERNAL,
    DeviceNamesControlChangeAction,
    DeviceNamesValueChangeAction,
    DeviceVendorsControlChangeAction,
    DeviceVendorsValueChangeAction,
    InterfaceLangsControlChangeAction,
    InterfaceLangsValueChangeAction,
    InternalNetworkControlChangeAction,
    IsBrowserControlChangeAction,
    IsMobileControlChangeAction,
    IsTabletControlChangeAction,
    IsTouchControlChangeAction,
    OPEN_ADDITIONAL_TARGETING_INTERNAL,
    OsFamiliesControlChangeAction,
    OsFamiliesValueChangeAction,
    OsNamesControlChangeAction,
    OsNamesValueChangeAction,
    PriorityValueChangeAction,
    QueryReferersControlChangeAction,
    QueryReferersValueChangeAction,
    SAVE_ADDITIONAL_TARGETING_CHANGES,
    SAVE_INTERNAL_GROUPS,
    UserAgentsControlChangeAction,
    UserAgentsValueChangeAction
} from '../../actions/groupsEditInternal';
import { mapJoinType, mapTargetingMode } from '../../mappers/internalGroup';
import { parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { PageStatus, ValidationErrorsData } from '../../typings/common';
import { EditGroupsState } from '../../typings/groupsEditState';
import { State } from '../../typings/state';
import { EditGroupsInitialState } from '../groupsEdit';

const getGroupsErrorsWithoutCurrentListErrors = (
    errors: ValidationErrorsData,
    currentListErrors: Array<string>
): ValidationErrorsData => {
    return Object.keys(errors).reduce((acc, errorKey) => {
        if (!currentListErrors.includes(errorKey)) {
            acc[errorKey] = errors[errorKey];
        }
        return acc;
    }, {});
};

const changeAdditionalTargetingUidsValue = (
    state: EditGroupsState,
    action: AdditionalTargetingUidsValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const { errors } = group;
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingYandexUids:
                action.value.length === 0
                    ? undefined
                    : {
                          joinType: mapJoinType(
                              value.targetingYandexUids,
                              serverValue.targetingYandexUids
                          ),
                          targetingMode: mapTargetingMode(
                              value.targetingYandexUids,
                              serverValue.targetingYandexUids
                          ),
                          value: action.value
                      }
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData,
                errors: getGroupsErrorsWithoutCurrentListErrors(errors, [
                    'targetingYandexUids'
                ])
            }
        }
    };
};

const changeAdditionalTargetingUidsControl = (
    state: EditGroupsState,
    action: AdditionalTargetingUidsControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }
    const { errors } = group;
    if (action.uncheckAll) {
        return {
            ...state,
            groups: {
                ...state.groups,
                [action.groupId]: {
                    ...state.groups[action.groupId],
                    value: {
                        ...value,
                        targetingYandexUids: undefined
                    },
                    errors: getGroupsErrorsWithoutCurrentListErrors(errors, [
                        'targetingYandexUids'
                    ])
                }
            }
        };
    } else {
        return {
            ...state,
            groups: {
                ...state.groups,
                [action.groupId]: {
                    ...state.groups[action.groupId],
                    value: {
                        ...value,
                        targetingYandexUids: {
                            value: value.targetingYandexUids
                                ? value.targetingYandexUids.value
                                : '',
                            joinType: action.joinType,
                            targetingMode: action.targetingMode
                        }
                    }
                }
            }
        };
    }
};

const cancelAdditionalTargetingChanges = (
    state: EditGroupsState,
    action: AdditionalTargetingChangesCancelAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );
    if (!serverValue) {
        throw new Error('invalid group type');
    }
    const { errors } = group;
    const groupValue = group.value;
    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...groupValue,
            targetingYandexUids: serverValue.targetingYandexUids
                ? {
                      ...serverValue.targetingYandexUids
                  }
                : undefined,
            targetingInternalNetwork: serverValue.targetingInternalNetwork
                ? {
                      ...serverValue.targetingInternalNetwork
                  }
                : undefined,
            targetingIsMobile: serverValue.targetingIsMobile
                ? {
                      ...serverValue.targetingIsMobile
                  }
                : undefined,
            targetingIsBrowser: serverValue.targetingIsBrowser
                ? {
                      ...serverValue.targetingIsBrowser
                  }
                : undefined,
            targetingIsTablet: serverValue.targetingIsTablet
                ? {
                      ...serverValue.targetingIsTablet
                  }
                : undefined,
            targetingIsTouch: serverValue.targetingIsTouch
                ? {
                      ...serverValue.targetingIsTouch
                  }
                : undefined,
            targetingOsNames: serverValue.targetingOsNames
                ? {
                      ...serverValue.targetingOsNames
                  }
                : undefined,
            targetingOsFamilies: serverValue.targetingOsFamilies
                ? {
                      ...serverValue.targetingOsFamilies
                  }
                : undefined,
            targetingBrowserNames: serverValue.targetingBrowserNames
                ? {
                      ...serverValue.targetingBrowserNames
                  }
                : undefined,
            targetingBrowserEngines: serverValue.targetingBrowserEngines
                ? {
                      ...serverValue.targetingBrowserEngines
                  }
                : undefined,
            targetingDeviceVendors: serverValue.targetingDeviceVendors
                ? {
                      ...serverValue.targetingDeviceVendors
                  }
                : undefined,
            targetingDeviceNames: serverValue.targetingDeviceNames
                ? {
                      ...serverValue.targetingDeviceNames
                  }
                : undefined,
            targetingUserAgents: serverValue.targetingUserAgents
                ? {
                      ...serverValue.targetingUserAgents
                  }
                : undefined,
            targetingQueryReferers: serverValue.targetingQueryReferers
                ? {
                      ...serverValue.targetingQueryReferers
                  }
                : undefined
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData,
                errors: getGroupsErrorsWithoutCurrentListErrors(errors, [
                    'targetingYandexUids'
                ])
            }
        }
    };
};

const saveAdditionalTargetingChanges = (
    state: EditGroupsState,
    action: AdditionalTargetingChangesSaveAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);

    if (!value) {
        throw new Error('invalid group type');
    }

    const newGroupData = {
        ...state.groups[action.groupId],
        serverValue: {
            ...state.groups[action.groupId].serverValue,
            targetingYandexUids: value.targetingYandexUids
                ? {
                      ...value.targetingYandexUids
                  }
                : undefined,
            targetingInternalNetwork: value.targetingInternalNetwork
                ? {
                      ...value.targetingInternalNetwork
                  }
                : undefined,
            targetingIsMobile: value.targetingIsMobile
                ? {
                      ...value.targetingIsMobile
                  }
                : undefined,
            targetingIsBrowser: value.targetingIsBrowser
                ? {
                      ...value.targetingIsBrowser
                  }
                : undefined,
            targetingIsTablet: value.targetingIsTablet
                ? {
                      ...value.targetingIsTablet
                  }
                : undefined,
            targetingIsTouch: value.targetingIsTouch
                ? {
                      ...value.targetingIsTouch
                  }
                : undefined,
            targetingOsNames: value.targetingOsNames
                ? {
                      ...value.targetingOsNames
                  }
                : undefined,
            targetingOsFamilies: value.targetingOsFamilies
                ? {
                      ...value.targetingOsFamilies
                  }
                : undefined,
            targetingBrowserNames: value.targetingBrowserNames
                ? {
                      ...value.targetingBrowserNames
                  }
                : undefined,
            targetingBrowserEngines: value.targetingBrowserEngines
                ? {
                      ...value.targetingBrowserEngines
                  }
                : undefined,
            targetingDeviceVendors: value.targetingDeviceVendors
                ? {
                      ...value.targetingDeviceVendors
                  }
                : undefined,
            targetingDeviceNames: value.targetingDeviceNames
                ? {
                      ...value.targetingDeviceNames
                  }
                : undefined,
            targetingUserAgents: value.targetingUserAgents
                ? {
                      ...value.targetingUserAgents
                  }
                : undefined,
            targetingQueryReferers: value.targetingQueryReferers
                ? {
                      ...value.targetingQueryReferers
                  }
                : undefined
        }
    };
    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeInternalNetworkControl = (
    state: EditGroupsState,
    action: InternalNetworkControlChangeAction
): EditGroupsState => {
    const targetingInternalNetwork = action.uncheckAll
        ? undefined
        : {
              joinType: action.joinType,
              targetingMode: action.targetingMode
          };

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingInternalNetwork
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeIsBrowserControl = (
    state: EditGroupsState,
    action: IsBrowserControlChangeAction
): EditGroupsState => {
    const targetingIsBrowser = action.uncheckAll
        ? undefined
        : {
              joinType: action.joinType,
              targetingMode: action.targetingMode
          };

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingIsBrowser
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeIsMobileControl = (
    state: EditGroupsState,
    action: IsMobileControlChangeAction
): EditGroupsState => {
    const targetingIsMobile = action.uncheckAll
        ? undefined
        : {
              joinType: action.joinType,
              targetingMode: action.targetingMode
          };

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingIsMobile
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeIsTabletControl = (
    state: EditGroupsState,
    action: IsTabletControlChangeAction
): EditGroupsState => {
    const targetingIsTablet = action.uncheckAll
        ? undefined
        : {
              joinType: action.joinType,
              targetingMode: action.targetingMode
          };

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingIsTablet
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeIsTouchControl = (
    state: EditGroupsState,
    action: IsTouchControlChangeAction
): EditGroupsState => {
    const targetingIsTouch = action.uncheckAll
        ? undefined
        : {
              joinType: action.joinType,
              targetingMode: action.targetingMode
          };

    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            targetingIsTouch
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData
            }
        }
    };
};

const changeOsNamesValue = (
    state: EditGroupsState,
    action: OsNamesValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingOsNames:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingOsNames,
                                      serverValue.targetingOsNames
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingOsNames,
                                      serverValue.targetingOsNames
                                  )
                              }
                }
            }
        }
    };
};

const changeOsNamesControl = (
    state: EditGroupsState,
    action: OsNamesControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingOsNames: {
                        value: value.targetingOsNames
                            ? value.targetingOsNames.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeOsFamiliesValue = (
    state: EditGroupsState,
    action: OsFamiliesValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingOsFamilies:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingOsFamilies,
                                      serverValue.targetingOsFamilies
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingOsFamilies,
                                      serverValue.targetingOsFamilies
                                  )
                              }
                }
            }
        }
    };
};

const changeOsFamiliesControl = (
    state: EditGroupsState,
    action: OsFamiliesControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingOsFamilies: {
                        value: value.targetingOsFamilies
                            ? value.targetingOsFamilies.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeBrowserNamesValue = (
    state: EditGroupsState,
    action: BrowserNamesValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingBrowserNames:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingBrowserNames,
                                      serverValue.targetingBrowserNames
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingBrowserNames,
                                      serverValue.targetingBrowserNames
                                  )
                              }
                }
            }
        }
    };
};

const changeBrowserNamesControl = (
    state: EditGroupsState,
    action: BrowserNamesControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingBrowserNames: {
                        value: value.targetingBrowserNames
                            ? value.targetingBrowserNames.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeBrowserEnginesValue = (
    state: EditGroupsState,
    action: BrowserEnginesValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingBrowserEngines:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingBrowserEngines,
                                      serverValue.targetingBrowserEngines
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingBrowserEngines,
                                      serverValue.targetingBrowserEngines
                                  )
                              }
                }
            }
        }
    };
};

const changeBrowserEnginesControl = (
    state: EditGroupsState,
    action: BrowserEnginesControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingBrowserEngines: {
                        value: value.targetingBrowserEngines
                            ? value.targetingBrowserEngines.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeDeviceVendorsValue = (
    state: EditGroupsState,
    action: DeviceVendorsValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingDeviceVendors:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingDeviceVendors,
                                      serverValue.targetingDeviceVendors
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingDeviceVendors,
                                      serverValue.targetingDeviceVendors
                                  )
                              }
                }
            }
        }
    };
};

const changeDeviceVendorsControl = (
    state: EditGroupsState,
    action: DeviceVendorsControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingDeviceVendors: {
                        value: value.targetingDeviceVendors
                            ? value.targetingDeviceVendors.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeDeviceNamesValue = (
    state: EditGroupsState,
    action: DeviceNamesValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingDeviceNames:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingDeviceNames,
                                      serverValue.targetingDeviceNames
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingDeviceNames,
                                      serverValue.targetingDeviceNames
                                  )
                              }
                }
            }
        }
    };
};

const changeDeviceNamesControl = (
    state: EditGroupsState,
    action: DeviceNamesControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingDeviceNames: {
                        value: value.targetingDeviceNames
                            ? value.targetingDeviceNames.value
                            : '',
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeInterfaceLangsValue = (
    state: EditGroupsState,
    action: InterfaceLangsValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingInterfaceLangs:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingInterfaceLangs,
                                      serverValue.targetingInterfaceLangs
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingInterfaceLangs,
                                      serverValue.targetingInterfaceLangs
                                  )
                              }
                }
            }
        }
    };
};

const changeInterfaceLangsControl = (
    state: EditGroupsState,
    action: InterfaceLangsControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingInterfaceLangs: {
                        value: value.targetingInterfaceLangs
                            ? value.targetingInterfaceLangs.value
                            : [],
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeQueryReferersValue = (
    state: EditGroupsState,
    action: QueryReferersValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingQueryReferers:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingQueryReferers,
                                      serverValue.targetingQueryReferers
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingQueryReferers,
                                      serverValue.targetingQueryReferers
                                  )
                              }
                }
            }
        }
    };
};

const changeQueryReferersControl = (
    state: EditGroupsState,
    action: QueryReferersControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingQueryReferers: {
                        value: value.targetingQueryReferers
                            ? value.targetingQueryReferers.value
                            : '',
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changeUserAgentsValue = (
    state: EditGroupsState,
    action: UserAgentsValueChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    let serverValue = GroupsHelpers.getEditGroupInternalDataValue(
        group.serverValue
    );

    if (!value) {
        throw new Error('invalid group type');
    }

    if (!serverValue) {
        serverValue = value;
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingUserAgents:
                        action.value.length === 0
                            ? undefined
                            : {
                                  value: action.value,
                                  joinType: mapJoinType(
                                      value.targetingUserAgents,
                                      serverValue.targetingUserAgents
                                  ),
                                  targetingMode: mapTargetingMode(
                                      value.targetingUserAgents,
                                      serverValue.targetingUserAgents
                                  )
                              }
                }
            }
        }
    };
};

const changeUserAgentsControl = (
    state: EditGroupsState,
    action: UserAgentsControlChangeAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...state.groups[action.groupId],
                value: {
                    ...value,
                    targetingUserAgents: {
                        value: value.targetingUserAgents
                            ? value.targetingUserAgents.value
                            : '',
                        joinType: action.joinType,
                        targetingMode: action.targetingMode
                    }
                }
            }
        }
    };
};

const changePriorityValue = (
    state: EditGroupsState,
    action: PriorityValueChangeAction
): EditGroupsState => {
    const { errors } = state.groups[action.groupId];
    const validLevel = parseNumber(action.value);
    if (validLevel > Number.MAX_SAFE_INTEGER || !Number.isInteger(validLevel)) {
        return {
            ...state
        };
    }
    const newGroupData = {
        ...state.groups[action.groupId],
        value: {
            ...state.groups[action.groupId].value,
            level: validLevel.toString()
        }
    };

    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...newGroupData,
                errors: getGroupsErrorsWithoutCurrentListErrors(errors, [
                    'level'
                ])
            }
        }
    };
};

const openAdditionalTargetingInternal = (
    state: EditGroupsState,
    action: AdditionalTargetingInternalOpenAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }
    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...group,
                value: {
                    ...value,
                    isAdditionalTargetingInternalOpen: true
                }
            }
        }
    };
};

const closeAdditionalTargetingInternal = (
    state: EditGroupsState,
    action: AdditionalTargetingInternalCloseAction
): EditGroupsState => {
    const group = state.groups[action.groupId];
    const value = GroupsHelpers.getEditGroupInternalDataValue(group.value);
    if (!value) {
        throw new Error('invalid group type');
    }
    return {
        ...state,
        groups: {
            ...state.groups,
            [action.groupId]: {
                ...group,
                value: {
                    ...value,
                    isAdditionalTargetingInternalOpen: false
                }
            }
        }
    };
};

export const groupsEditInternal = (
    state: EditGroupsState = EditGroupsInitialState,
    action: Action,
    fullState: State
): EditGroupsState => {
    switch (action.type) {
        case SAVE_INTERNAL_GROUPS:
            return {
                ...state,
                errors: [],
                pageStatus: PageStatus.SAVING
            };
        case CHANGE_ADDITIONAL_TARGETING_UIDS_VALUE:
            return changeAdditionalTargetingUidsValue(state, action);
        case CHANGE_ADDITIONAL_TARGETING_UIDS_CONTROL:
            return changeAdditionalTargetingUidsControl(state, action);
        case CANCEL_ADDITIONAL_TARGETING_CHANGES:
            return cancelAdditionalTargetingChanges(state, action);
        case SAVE_ADDITIONAL_TARGETING_CHANGES:
            return saveAdditionalTargetingChanges(state, action);
        case CHANGE_INTERNAL_NETWORK_CONTROL:
            return changeInternalNetworkControl(state, action);
        case CHANGE_IS_BROWSER_CONTROL:
            return changeIsBrowserControl(state, action);
        case CHANGE_IS_MOBILE_CONTROL:
            return changeIsMobileControl(state, action);
        case CHANGE_IS_TOUCH_CONTROL:
            return changeIsTouchControl(state, action);
        case CHANGE_IS_TABLET_CONTROL:
            return changeIsTabletControl(state, action);
        case CHANGE_OS_NAMES_VALUE:
            return changeOsNamesValue(state, action);
        case CHANGE_OS_NAMES_CONTROL:
            return changeOsNamesControl(state, action);
        case CHANGE_OS_FAMILIES_VALUE:
            return changeOsFamiliesValue(state, action);
        case CHANGE_OS_FAMILIES_CONTROL:
            return changeOsFamiliesControl(state, action);
        case CHANGE_BROWSER_NAMES_VALUE:
            return changeBrowserNamesValue(state, action);
        case CHANGE_BROWSER_NAMES_CONTROL:
            return changeBrowserNamesControl(state, action);
        case CHANGE_BROWSER_ENGINES_VALUE:
            return changeBrowserEnginesValue(state, action);
        case CHANGE_BROWSER_ENGINES_CONTROL:
            return changeBrowserEnginesControl(state, action);
        case CHANGE_DEVICE_VENDORS_VALUE:
            return changeDeviceVendorsValue(state, action);
        case CHANGE_DEVICE_VENDORS_CONTROL:
            return changeDeviceVendorsControl(state, action);
        case CHANGE_DEVICE_NAMES_VALUE:
            return changeDeviceNamesValue(state, action);
        case CHANGE_DEVICE_NAMES_CONTROL:
            return changeDeviceNamesControl(state, action);
        case CHANGE_INTERFACE_LANGS_VALUE:
            return changeInterfaceLangsValue(state, action);
        case CHANGE_INTERFACE_LANGS_CONTROL:
            return changeInterfaceLangsControl(state, action);
        case CHANGE_QUERY_REFERERS_VALUE:
            return changeQueryReferersValue(state, action);
        case CHANGE_QUERY_REFERERS_CONTROL:
            return changeQueryReferersControl(state, action);
        case CHANGE_USER_AGENTS_VALUE:
            return changeUserAgentsValue(state, action);
        case CHANGE_USER_AGENTS_CONTROL:
            return changeUserAgentsControl(state, action);
        case CHANGE_PRIORITY_VALUE:
            return changePriorityValue(state, action);
        case OPEN_ADDITIONAL_TARGETING_INTERNAL:
            return openAdditionalTargetingInternal(state, action);
        case CLOSE_ADDITIONAL_TARGETING_INTERNAL:
            return closeAdditionalTargetingInternal(state, action);
        default:
            return state;
    }
};
